
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { NavHeader } from "@/components/layout/nav-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";

interface GofundmeLink {
  id: number;
  name: string;
  link: string;
  instagramUrl?: string;
}

export default function AdminGofundme() {
  const [editingLink, setEditingLink] = useState<GofundmeLink | null>(null);
  const [newLink, setNewLink] = useState({ name: "", link: "", instagramUrl: "" });
  const [showNewLinkDialog, setShowNewLinkDialog] = useState(false);
  const queryClient = useQueryClient();

  const { data: links = [] } = useQuery({
    queryKey: ["gofundme-links"],
    queryFn: async () => {
      const res = await fetch("/api/admin/gofundme");
      if (!res.ok) throw new Error("Failed to fetch links");
      return res.json();
    },
  });

  const createLinkMutation = useMutation({
    mutationFn: async (linkData: Omit<GofundmeLink, "id">) => {
      const res = await fetch("/api/admin/gofundme", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(linkData),
      });
      if (!res.ok) throw new Error("Failed to create link");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["gofundme-links"]);
      setShowNewLinkDialog(false);
      setNewLink({ name: "", link: "", instagramUrl: "" });
      toast({ title: "Link created successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error creating link",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const updateLinkMutation = useMutation({
    mutationFn: async (linkData: GofundmeLink) => {
      const res = await fetch(`/api/admin/gofundme/${linkData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(linkData),
      });
      if (!res.ok) throw new Error("Failed to update link");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["gofundme-links"]);
      setEditingLink(null);
      toast({ title: "Link updated successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error updating link",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/gofundme/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete link");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["gofundme-links"]);
      toast({ title: "Link deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error deleting link",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  return (
    <div className="min-h-screen bg-clubhouse-black">
      <NavHeader />
      <main className="container mx-auto px-4 py-8 animate-page-in">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Manage GoFundMe Links</h1>
          <Button 
            onClick={() => setShowNewLinkDialog(true)}
            className="bg-white hover:bg-white/90 text-black"
          >
            Add New Link
          </Button>
        </div>
        
        <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden border border-white/10">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/40 font-normal">Name</TableHead>
                <TableHead className="text-white/40 font-normal">GoFundMe Link</TableHead>
                <TableHead className="text-white/40 font-normal">Instagram</TableHead>
                <TableHead className="text-white/40 font-normal">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((link: GofundmeLink) => (
                <TableRow key={link.id} className="border-white/10">
                  <TableCell className="text-white">{link.name}</TableCell>
                  <TableCell>
                    <a href={link.link} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:underline">
                      {link.link}
                    </a>
                  </TableCell>
                  <TableCell>
                    {link.instagramUrl ? (
                      <a href={link.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:underline">
                        {link.instagramUrl}
                      </a>
                    ) : (
                      <span className="text-white/40">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingLink(link)}
                        className="text-white/40 hover:text-white"
                      >
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Link</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this link? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteLinkMutation.mutate(link.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Edit Link Dialog */}
        <Dialog open={!!editingLink} onOpenChange={() => setEditingLink(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Link</DialogTitle>
            </DialogHeader>
            {editingLink && (
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium text-white">Name</label>
                  <Input
                    value={editingLink.name}
                    onChange={(e) =>
                      setEditingLink({ ...editingLink, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-white">GoFundMe Link</label>
                  <Input
                    value={editingLink.link}
                    onChange={(e) =>
                      setEditingLink({ ...editingLink, link: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-white">Instagram URL (optional)</label>
                  <Input
                    value={editingLink.instagramUrl || ''}
                    onChange={(e) =>
                      setEditingLink({ ...editingLink, instagramUrl: e.target.value })
                    }
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <Button
                  className="w-full bg-white hover:bg-white/90 text-black"
                  onClick={() => updateLinkMutation.mutate(editingLink)}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* New Link Dialog */}
        <Dialog open={showNewLinkDialog} onOpenChange={setShowNewLinkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Link</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-white">Name</label>
                <Input
                  value={newLink.name}
                  onChange={(e) =>
                    setNewLink({ ...newLink, name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium text-white">GoFundMe Link</label>
                <Input
                  value={newLink.link}
                  onChange={(e) =>
                    setNewLink({ ...newLink, link: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium text-white">Instagram URL (optional)</label>
                <Input
                  value={newLink.instagramUrl}
                  onChange={(e) =>
                    setNewLink({ ...newLink, instagramUrl: e.target.value })
                  }
                  placeholder="https://instagram.com/..."
                />
              </div>
              <Button
                className="w-full bg-white hover:bg-white/90 text-black"
                onClick={() => createLinkMutation.mutate(newLink)}
              >
                Create Link
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
